import { Link } from 'react-router-dom';
import type { OverviewResponse } from '@hospasense/contracts';
import { LIFECYCLE_LABELS } from '@hospasense/contracts';
import { useOverview } from '../lib/queries.js';
import { formatDateTime, relativeTime, titleCase } from '../lib/format.js';
import { useAuth } from '../lib/auth.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  HealthPill,
  LifecyclePill,
  LoadingState,
  PageHeader,
  PermissionDeniedState,
} from '../components/ui/index.js';

/** Metric value with its definition, source and freshness always reachable. */
function Metric({
  label,
  value,
  meta,
  tone,
}: {
  label: string;
  value: number;
  meta: { definition: string; source: string; computedAt: string; drillDownPath: string | null };
  tone?: 'warning' | 'error';
}) {
  const body = (
    <span
      className="block rounded-lg px-3 py-2 transition-colors duration-150 hover:bg-bg-subtle"
      title={`${meta.definition}\nSource: ${meta.source}\nComputed: ${formatDateTime(meta.computedAt)}`}
    >
      <span
        className={`block text-2xl font-semibold tabular-nums ${
          tone === 'warning' && value > 0
            ? 'text-warning'
            : tone === 'error' && value > 0
              ? 'text-error'
              : 'text-text-primary'
        }`}
      >
        {value}
      </span>
      <span className="block text-xs text-text-muted">{label}</span>
    </span>
  );
  return meta.drillDownPath ? (
    <Link
      to={meta.drillDownPath}
      className="rounded-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      aria-label={`${label}: ${value}. ${meta.definition} Open filtered list.`}
    >
      {body}
    </Link>
  ) : (
    body
  );
}

export function OverviewPage() {
  const { hasPermission } = useAuth();
  const query = useOverview();

  if (!hasPermission('overview.read')) {
    return <PermissionDeniedState what="Mission Overview" />;
  }
  if (query.isLoading) return <LoadingState label="Loading Mission Overview…" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        description="The overview could not be loaded from the Control API."
        onRetry={() => void query.refetch()}
      />
    );
  }
  const data: OverviewResponse = query.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mission Overview"
        subtitle={`Synthetic portfolio · computed ${relativeTime(data.generatedAt)} · every figure drills into the registry`}
        breadcrumbs={<Breadcrumbs items={[{ label: 'Control' }, { label: 'Mission Overview' }]} />}
      />

      <section aria-label="Portfolio by lifecycle state">
        <Card>
          <CardHeader
            title="Portfolio by lifecycle"
            subtitle="Active organisations per validated lifecycle state"
          />
          <CardBody>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              {data.portfolioByLifecycle.map((entry) => (
                <Metric
                  key={entry.state}
                  label={LIFECYCLE_LABELS[entry.state]}
                  value={entry.count}
                  meta={entry.meta}
                />
              ))}
            </div>
          </CardBody>
        </Card>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-label="Deployments and installation health">
          <Card>
            <CardHeader title="Deployments" subtitle="Cloud vs on-prem, installation health" />
            <CardBody className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <Metric
                  label="Cloud"
                  value={data.deployments.cloud.value}
                  meta={data.deployments.cloud.meta}
                />
                <Metric
                  label="On-prem"
                  value={data.deployments.onPrem.value}
                  meta={data.deployments.onPrem.meta}
                />
              </div>
              <div className="border-t border-border pt-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                  Installations by last reported health
                </p>
                <ul className="space-y-1.5">
                  {data.installationsByHealth.map((h) => (
                    <li
                      key={h.health}
                      className="flex items-center justify-between text-sm"
                      title={`${h.meta.definition}\nSource: ${h.meta.source}`}
                    >
                      <HealthPill health={h.health} />
                      <span className="tabular-nums font-medium">{h.count}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-text-muted">
                  Source: signed heartbeat webhooks. “Unknown” means no heartbeat received — treat
                  the data as stale, not healthy.
                </p>
              </div>
            </CardBody>
          </Card>
        </section>

        <section aria-label="Needs attention">
          <Card>
            <CardHeader
              title="Needs attention"
              subtitle="Stale or incomplete records, shown explicitly"
            />
            <CardBody>
              <div className="grid grid-cols-3 gap-2">
                <Metric
                  label="Stale records"
                  value={data.attention.staleOrganisations.value}
                  meta={data.attention.staleOrganisations.meta}
                  tone="warning"
                />
                <Metric
                  label="No contacts"
                  value={data.attention.missingContacts.value}
                  meta={data.attention.missingContacts.meta}
                  tone="warning"
                />
                <Metric
                  label="Unowned"
                  value={data.attention.unowned.value}
                  meta={data.attention.unowned.meta}
                  tone="error"
                />
              </div>
              <p className="mt-3 text-xs text-text-muted">
                Hover any figure for its definition, source and freshness; click to open the exact
                filtered list behind it.
              </p>
            </CardBody>
          </Card>
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-label="Recent lifecycle changes">
          <Card>
            <CardHeader title="Recent lifecycle changes" subtitle="From the immutable history" />
            <CardBody>
              {data.recentLifecycleEvents.length === 0 ? (
                <p className="text-sm text-text-muted">No lifecycle changes recorded yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {data.recentLifecycleEvents.map((e) => (
                    <li key={e.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                      <div className="min-w-0">
                        <Link
                          to={`/organisations/${e.organisationId}`}
                          className="font-medium text-link hover:underline focus-visible:ring-2 focus-visible:ring-ring rounded-sm focus-visible:outline-none"
                        >
                          {e.organisationName}
                        </Link>
                        <p className="text-xs text-text-muted">
                          {e.fromState ? `${LIFECYCLE_LABELS[e.fromState]} → ` : ''}
                          {LIFECYCLE_LABELS[e.toState]}
                          {e.actorDisplayName ? ` · ${e.actorDisplayName}` : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <LifecyclePill state={e.toState} />
                        <span className="text-xs text-text-muted">
                          {relativeTime(e.occurredAt)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </section>

        <section aria-label="Recent operator activity">
          <Card>
            <CardHeader
              title="Recent operator activity"
              subtitle="From the append-only audit log"
            />
            <CardBody>
              {data.recentOperatorActivity.length === 0 ? (
                <p className="text-sm text-text-muted">No operator activity recorded yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {data.recentOperatorActivity.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate">
                          <span className="font-medium">{a.actorDisplayName ?? 'System'}</span>{' '}
                          <span className="text-text-secondary">
                            {titleCase(a.action.replace(/\./g, ' '))}
                          </span>
                        </p>
                        {a.objectLabel && (
                          <p className="truncate text-xs text-text-muted">{a.objectLabel}</p>
                        )}
                      </div>
                      <span className="shrink-0 text-xs text-text-muted">
                        {relativeTime(a.occurredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </section>
      </div>
    </div>
  );
}
